<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl group">
      <inertia-link
        class="flex text-indigo-400 group-hover:text-indigo-600"
        :href="route('questionnaires')"
      >
        <icon
          name="questionnaires"
          class="w-6 h-9 mr-3 fill-indigo-400 group-hover:fill-indigo-600"
        />
        Questionários /
      </inertia-link>
      <span class="ml-1 font-medium">
        {{ form.title }}
      </span>
    </h1>

    <div class="bg-white rounded-md shadow overflow-hidden w-full">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <text-input
            v-model="form.title"
            :error="form.errors.title"
            class="pr-6 pb-8 w-full lg:w-1/2"
            :class="{ 'lg:w-1/2': !form.intelligence, 'lg:w-1/4': form.intelligence }"
            label="Título"
          />

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label for="state" class="form-label">Estado:</label>

            <select
              id="state"
              v-model="form.state"
              class="form-control form-select"
              :class="{ error: form.errors.state }"
              @change="getCities()"
            >
              <option :value="null" />
              <option v-for="state in states" :key="state.id" :value="state.id">
                {{ state.name }}
              </option>
            </select>

            <div v-if="form.errors.state" class="form-error">
              {{ form.errors.state }}
            </div>
          </div>

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label for="city" class="form-label">Cidade:</label>

            <select
              id="city"
              v-model="form.city"
              class="form-control form-select"
              :class="{ error: form.errors.city }"
            >
              <option :value="null" />
              <option v-for="city in userCities" :key="city.id" :value="city.id">
                {{ city.name }}
              </option>
            </select>

            <div v-if="form.errors.city" class="form-error">
              {{ form.errors.city }}
            </div>
          </div>

          <FileInputBase64
            v-model="form.logo"
            :error="form.errors.logo"
            class="pr-6 pb-8 w-full lg:w-1/4"
            :class="{ hidden: !form.intelligence }"
            accept="image/*"
            label="Logo"
            :imgEdited="propForInputBase64"
          />

          <text-input
            v-model="form.opened_on"
            :error="
              form.errors.opened_on ? 'O campo &quot;disponível em&quot; é obrigatório.' : null
            "
            type="date"
            class="pr-6 pb-8 w-full lg:w-1/4"
            label="Disponível a partir de"
          />

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label for="organization" class="form-label">Organização:</label>

            <select
              id="organization"
              v-model="form.organization_id"
              class="form-control form-select"
              :class="{ error: form.errors.organization_id }"
            >
              <option :value="null" />
              <option
                v-for="organization in organizations"
                :key="organization.id"
                :value="organization.id"
              >
                {{ organization.name }}
              </option>
            </select>

            <div v-if="form.errors.organization_id" class="form-error">
              {{ form.errors.organization_id }}
            </div>
          </div>

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label for="role" class="form-label">Papel:</label>

            <select
              id="role"
              v-model="form.role_id"
              class="form-control form-select"
              :class="{ error: form.errors.role_id }"
            >
              <option :value="null" />
              <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
            </select>

            <div v-if="form.errors.role_id" class="form-error">
              {{ form.errors.role_id }}
            </div>
          </div>

          <div class="pr-6 pb-8 w-full lg:w-1/4">
            <label for="intelligence" class="form-label">Questionário Inteligente:</label>

            <button
              id="intelligence"
              type="button"
              class="form-control w-full btn-indigo"
              @click="setIntelligence"
            >
              <font-awesome-icon icon="rotate" /> {{ form.intelligence ? 'Ativado' : 'Desativado' }}
            </button>

            <input id="f_intelligence" v-model="form.intelligence" class="hidden" type="checkbox" />
          </div>

          <div class="pr-6 pb-8 w-full">
            <textarea-input
              id="header"
              v-model="form.header"
              readonly
              label="Instruções e/ou informações"
              @callbackClick="openNewInstruction"
            />
            <modal
              v-show="newInstructionsStatement"
              v-model="form.header"
              context="current"
              @close="setNewInstructionStatementPlaceholder"
            />
          </div>

          <div class="pr-6 pb-8 w-full">
            <text-input
              v-model="form.footer"
              label="Mensagem final de formulário"
              readonly
              @callbackClick="showModalFooter"
            />
            <modal
              v-show="modal_footer"
              v-model="form.footer"
              context="current"
              @close="setModal_footer_statement"
            />
          </div>
        </div>

        <div class="p-8 -mr-6 -mb-8">
          <div class="pr-6 pb-8 w-full">
            <label class="form-label" for="url">
              URL do Questionário: <small v-if="copied" class="text-indigo">Copiado!</small>
            </label>

            <span
              id="url"
              class="text-xs font-bold bg-gray-100 text-gray-600 rounded cursor-pointer"
              @click="copy(questionnaire.route)"
            >
              {{ questionnaire.route }}
            </span>
          </div>
        </div>

        <div class="p-8 -mr-6 -mb-8">
          <label class="form-label">Perguntas:</label>
          <Accordion>
            <AccordionTab
              v-for="(topicData, topicIndex) in topicsData"
              :key="`topic-${topicData.id}`"
            >
              <template #header>
                <font-awesome-icon :icon="topicData.icon" size="lg" class="mr-3" />
                <span class="mr-10">{{ topicData.name.split(/\n/g)[0] }}</span>
                <span
                  >Perguntas:
                  <span class="text-green-600">{{ topicData.questionsData.length }}</span></span
                >
              </template>
              <div>
                <button
                  type="button"
                  class="btn-indigo"
                  @click="editTopic(topicData.id, topicData.icon, topicData.name, topicIndex)"
                >
                  Editar tópico
                </button>
                <button type="button" class="btn-gray" @click="removeTopic(topicData.id)">
                  Remover tópico
                </button>
                <!-- <button
                  type="button"
                  class="btn-gray"
                  @click="destroyTopic(topicData.id, topicIndex)"
                >
                  Excluir tópico
                </button> -->
              </div>
              <div v-if="editingTopic == topicData.id">
                <text-input v-model="currentTopic.name" class="pr-6 pb-8 w-1/2" label="Nome" />

                <text-input v-model="currentTopic.icon" class="pr-2 pb-8 w-1/2" label="Ícone" />
                <button
                  type="button"
                  class="btn-indigo"
                  :disabled="currentTopicRequest"
                  @click="updateCurrentTopic"
                >
                  Salvar
                </button>
                <button type="button" class="btn-gray" @click="resetCurrentTopicForm">
                  Cancelar
                </button>
              </div>
              <div>
                <ul>
                  <li
                    v-for="(questionData, questionIndex) in topicData.questionsData"
                    :key="`question-${questionData.id}-${topicData.id}`"
                    class="my-4 pb-3 border-b border-gray-400"
                  >
                    <div class="flex items-center mb-2" v-if="editingQuestion !== questionData.id">
                      <p
                        class="text-xl font-medium text-green-400s"
                        :class="{ 'hover:text-indigo cursor-pointer': !editingQuestion }"
                        @click="
                          editQuestion(
                            questionData.id,
                            topicData.id,
                            questionData.statement,
                            questionData.left_label,
                            questionData.right_label,
                            questionData.middle_label,
                            questionData.type,
                            questionData.required,
                            questionData.options,
                            questionData.slider_text_zero,
                            questionData.slider_text_one,
                            questionData.slider_text_two,
                            questionData.slider_text_three,
                            questionData.slider_text_four,
                            questionData.slider_text_five,
                            questionIndex,
                          )
                        "
                      >
                        {{ questionData.statement.split(/\n/g)[0] }}
                      </p>
                      <button
                        class="hover:underline ml-5 mr-2 my-auto"
                        tabindex="-1"
                        type="button"
                        @click="destroyQuestion(questionData.id, topicData.id, questionIndex)"
                      >
                        <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
                      </button>
                    </div>
                    <div class="my-5" v-else>
                      <div class="flex flex-wrap">
                        <div class="w-1/3" @click="openCurrentQuestionStatement">
                          <text-input
                            v-model="currentQuestion.placeholder"
                            class="w-full"
                            label="Pergunta"
                            readonly
                          />
                        </div>

                        <modal
                          v-show="currentQuestionStatement"
                          v-model="currentQuestion.statement"
                          context="current"
                          @close="setCurrentQuestionPlaceholder"
                        />

                        <select-input
                          v-model="currentQuestion.type"
                          class="w-1/4 ml-2"
                          label="Tipo"
                          @change="
                            removeQuestionOptions($event, questionData.id, questionData.type)
                          "
                        >
                          <option :value="null" />
                          <option value="NUMBER">Nota (1 a 5)</option>
                          <option value="RADIO">Escolha Única</option>
                          <option value="CHECK">Seleção Múltipla</option>
                          <option value="LINE">Texto de Linha Única</option>
                          <option value="MULTI">Texto Multilinha</option>
                          <option value="SLIDER">Controle Deslizante</option>
                        </select-input>

                        <button
                          class="
                            px-6
                            py-3
                            rounded
                            text-white text-sm
                            leading-4
                            font-bold
                            whitespace-nowrap
                            w-1/8
                            h-1/2
                            mt-auto
                            ml-1
                          "
                          :class="{
                            'bg-indigo-400': !currentQuestion.required,
                            'hover:bg-indigo-600': !currentQuestion.required,
                            'focus:bg-indigo-600': !currentQuestion.required,
                            'bg-red-500': currentQuestion.required,
                            'hover:bg-red-600': currentQuestion.required,
                            'focus:bg-red-600': currentQuestion.required,
                          }"
                          type="button"
                          @click="invertCurrentQuestionRequeriment"
                        >
                          <font-awesome-icon
                            :icon="currentQuestion.required ? 'asterisk' : 'minus'"
                          />
                        </button>

                        <button
                          class="btn-indigo w-1/8 h-1/2 mt-auto ml-2 pl-5"
                          type="button"
                          :disabled="currentQuestionRequest"
                          @click="updateCurrentQuestion"
                        >
                          Salvar
                        </button>

                        <button
                          class="btn-gray w-1/8 h-1/2 mt-auto ml-2"
                          type="button"
                          @click="resetCurrentQuestionForm"
                        >
                          Cancelar
                        </button>
                      </div>
                      <!--========== NUMBER EDITING ==========-->
                      <div v-if="currentQuestion.type === 'NUMBER'" class="mt-3 flex">
                        <text-input
                          v-model="currentQuestion.left_text"
                          class="mr-2 w-1/2"
                          label="Texto à esquerda"
                        />

                        <text-input
                          v-model="currentQuestion.right_text"
                          class="w-1/2"
                          label="Texto à direita"
                        />
                      </div>
                      <!--========== SLIDER EDITING ==========-->
                      <div v-if="currentQuestion.type === 'SLIDER'" class="mt-3 flex flex-col">
                        <text-input
                          v-model="currentQuestion.text0"
                          class="w-full mb-4"
                          label="Texto 0"
                        />
                        <text-input
                          v-model="currentQuestion.text1"
                          class="w-full mb-4"
                          label="Texto 1"
                        />
                        <text-input
                          v-model="currentQuestion.text2"
                          class="w-full mb-4"
                          label="Texto 2"
                        />
                        <text-input
                          v-model="currentQuestion.text3"
                          class="w-full mb-4"
                          label="Texto 3"
                        />
                        <text-input
                          v-model="currentQuestion.text4"
                          class="w-full mb-4"
                          label="Texto 4"
                        />
                        <text-input
                          v-model="currentQuestion.text5"
                          class="w-full mb-4"
                          label="Texto 5"
                        />
                      </div>
                      <!--========== ALTERNATIVES EDITING ==========-->
                      <div
                        v-if="
                          currentQuestion.type === 'CHECK' ||
                          currentQuestion.type === 'RADIO' ||
                          currentQuestion.type === 'SLIDER'
                        "
                        class="my-4"
                      >
                        <ul class="list-disc list-insid ml-5">
                          <li
                            v-for="(option, optionIndex) in currentQuestion.options"
                            :key="optionIndex"
                            class="my-4"
                          >
                            <span
                              @click="editQuestionOption(optionIndex, questionIndex, topicIndex)"
                              class="cursor-pointer"
                            >
                              {{ option.alternative }}
                            </span>
                            <button
                              v-if="editingQuestion && editingQuestion === questionData.id"
                              class="hover:underline ml-5 mr-2 my-auto"
                              tabindex="-1"
                              type="button"
                              @click="destroyQuestionOption(option.id, optionIndex, questionData)"
                            >
                              <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
                            </button>
                          </li>
                        </ul>
                        <div v-if="flagAuxEditExistingOption">
                          <text-input
                            v-model="currentAlternative.option"
                            class="w-1/3"
                            label="Alternativa"
                          />

                          <button
                            class="btn-indigo w-1/8 h-1/2 mt-auto"
                            type="button"
                            @click="updateCurrentAlternative"
                          >
                            Salvar
                          </button>
                          <button
                            class="btn-gray w-1/8 h-1/2 mt-auto ml-1"
                            type="button"
                            @click="flagAuxEditExistingOption = false"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                    <!--========== DISPLAY INFOS ==========-->
                    <div>
                      <p class="my-3 inline-block">
                        Tipo:
                        <span class="text-green-400 font-medium">{{
                          getQuestionType(questionData.type)
                        }}</span>
                      </p>
                      <p class="inline-block ml-8">
                        {{ questionData.required ? 'Obrigatória' : 'Opcional' }}
                      </p>
                    </div>
                    <div
                      class="my-1"
                      v-if="
                        (questionData.type === 'RADIO' ||
                          questionData.type === 'CHECK' ||
                          questionData.type === 'SLIDER') &&
                        !editingQuestion
                      "
                    >
                      <ul class="list-disc list-inside">
                        <li
                          v-for="(option, option_index) in questionData.options"
                          :key="option_index"
                          class="my-4"
                        >
                          {{ option.alternative }}
                        </li>
                      </ul>
                    </div>
                    <div class="my-1" v-if="questionData.type === 'SLIDER'">
                      <p class="my-1">Label 0: {{ questionData.slider_text_zero }}</p>
                      <p class="my-1">Label 1: {{ questionData.slider_text_one }}</p>
                      <p class="my-1">Label 2: {{ questionData.slider_text_two }}</p>
                      <p class="my-1">Label 3: {{ questionData.slider_text_three }}</p>
                      <p class="my-1">Label 4: {{ questionData.slider_text_four }}</p>
                      <p class="my-1">Label 5: {{ questionData.slider_text_five }}</p>
                    </div>
                    <div class="my-1">
                      <ul v-if="questionData.type === 'NUMBER'" class="list-disc list-inside">
                        <li class="my-4">
                          <span>Texto a direita: </span>
                          {{ questionData.right_label || questionData.right_text }}
                        </li>
                        <li class="my-4">
                          <span>Texto a esquerda: </span>
                          {{ questionData.left_label || questionData.left_text }}
                        </li>
                      </ul>
                    </div>
                  </li>
                  <!--========== ADD QUESTION ==========-->
                  <li>
                    <p
                      @click="makeNewQuestion(topicData.id)"
                      class="text-xl font-medium cursor-pointer my-4 hover:text-indigo"
                    >
                      Adicionar nova pergunta +
                    </p>
                    <div class="flex my-2 items-end" v-if="newQuestion.topic == topicData.id">
                      <text-input
                        label="Pergunta"
                        class="pr-6 w-1/3"
                        v-model="newQuestion.statement"
                        readonly
                        @callbackClick="showModalNewQuestionStatement"
                      />
                      <modal
                        v-show="newQuestionStatement_aux"
                        v-model="newQuestion.statement"
                        context="current"
                        @close="setModal_newQuestionStatement"
                      />
                      <select-input class="w-1/3" label="Tipo" v-model="newQuestion.type">
                        <option :value="null" />
                        <option value="NUMBER">Nota (1 a 5)</option>
                        <option value="RADIO">Escolha Única</option>
                        <option value="CHECK">Seleção Múltipla</option>
                        <option value="LINE">Texto de Linha Única</option>
                        <option value="MULTI">Texto Multilinha</option>
                        <option value="SLIDER">Controle Deslizante</option>
                      </select-input>
                      <button
                        class="
                          px-6
                          py-3
                          rounded
                          text-white text-sm
                          font-bold
                          whitespace-nowrap
                          w-1/8
                          h-1/2
                          ml-3
                        "
                        :class="{
                          'bg-indigo-400': !newQuestion.required,
                          'hover:bg-indigo-600': !newQuestion.required,
                          'focus:bg-indigo-600': !newQuestion.required,
                          'bg-red-500': newQuestion.required,
                          'hover:bg-red-600': newQuestion.required,
                          'focus:bg-red-600': newQuestion.required,
                        }"
                        type="button"
                        @click="invertNewQuestionRequeriment"
                      >
                        <font-awesome-icon :icon="newQuestion.required ? 'asterisk' : 'minus'" />
                      </button>
                    </div>
                    <div
                      v-if="newQuestion.topic == topicData.id && newQuestion.type === 'NUMBER'"
                      class="flex"
                    >
                      <text-input
                        v-model="newQuestion.left_label"
                        class="mr-2 w-1/3"
                        label="Texto à esquerda"
                      />
                      <text-input
                        v-model="newQuestion.right_label"
                        class="w-1/3"
                        label="Texto à direita"
                      />
                    </div>
                    <div
                      v-if="
                        newQuestion.topic == topicData.id &&
                        (newQuestion.type === 'RADIO' ||
                          newQuestion.type === 'CHECK' ||
                          newQuestion.type === 'SLIDER')
                      "
                      class="mt-3"
                    >
                      <p class="my-1">Lista de alternativas</p>
                      <ul
                        v-if="
                          creatingAlternative ||
                          (newQuestion.options && newQuestion.options.length > 0)
                        "
                        class="space-y-1 mb-3 list-disc list-inside"
                      >
                        <li
                          v-for="(option, optionIndex) in newQuestion.options"
                          :key="`alternative-${option}-${optionIndex}`"
                          :class="{
                            'no-dot':
                              editingAlternative && currentAlternative.index === optionIndex,
                          }"
                        >
                          <span v-if="currentAlternative.index !== optionIndex">
                            <span
                              :class="{ 'hover:text-indigo cursor-pointer': !editingAlternative }"
                              @click="editNewQuestionOption(option, optionIndex)"
                            >
                              {{ option }}
                            </span>

                            <button
                              class="hover:underline ml-5 mr-2 my-auto"
                              tabindex="-1"
                              type="button"
                              @click="destroyNewQuestionOption(optionIndex)"
                            >
                              <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
                            </button>
                          </span>
                          <div v-else class="flex">
                            <text-input
                              v-model="currentAlternative.option"
                              class="w-1/3"
                              label="Alternativa"
                            />
                            <button
                              class="btn-indigo w-1/8 h-1/2 mt-auto ml-1"
                              type="button"
                              @click="storeNewQuestionCurrentOption"
                            >
                              Salvar
                            </button>
                          </div>
                        </li>

                        <li v-if="creatingAlternative" class="no-dot flex">
                          <text-input
                            v-model="newAlternative.option"
                            class="w-1/3"
                            label="Alternativa"
                          />
                          <button
                            class="btn-indigo w-1/8 h-1/2 mt-auto ml-1"
                            type="button"
                            @click="storeNewQuestionOption"
                          >
                            Salvar
                          </button>
                        </li>
                      </ul>

                      <button
                        v-if="!creatingAlternative"
                        class="btn-gray text-xs p-2 mb-4"
                        type="button"
                        @click="addOptionToNewQuestion"
                      >
                        + Alternativa
                      </button>
                    </div>
                    <div v-if="newQuestion.topic == topicData.id && newQuestion.type === 'SLIDER'">
                      <text-input v-model="newQuestion.text0" class="w-full mb-4" label="Texto 0" />

                      <text-input v-model="newQuestion.text1" class="w-full mb-4" label="Texto 1" />

                      <text-input v-model="newQuestion.text2" class="w-full mb-4" label="Texto 2" />

                      <text-input v-model="newQuestion.text3" class="w-full mb-4" label="Texto 3" />

                      <text-input v-model="newQuestion.text4" class="w-full mb-4" label="Texto 4" />

                      <text-input v-model="newQuestion.text5" class="w-full mb-4" label="Texto 5" />
                    </div>
                    <div class="mt-3" v-if="newQuestion.topic == topicData.id">
                      <button
                        class="btn-indigo w-1/8 h-1/2 mt-auto"
                        type="button"
                        :disabled="newQuestionRequest"
                        @click="storeNewQuestion"
                      >
                        Salvar
                      </button>

                      <button
                        class="btn-gray w-1/8 h-1/2 mt-auto ml-2"
                        type="button"
                        @click="resetQuestionForm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </li>
                  <!-- LI FINAL DE CRIAÇÃO -->
                </ul>
              </div>
            </AccordionTab>
          </Accordion>
        </div>

        <div class="p-8 -mr-6 -mb-8">
          <button
            v-if="!selectingTopicOption"
            class="btn-gray"
            type="button"
            @click="selectingTopicOption = true"
          >
            + Tópico
          </button>

          <div v-if="selectingTopicOption">
            <span class="font-medium"> Novo Tópico </span>

            <div class="mt-4 mb-8">
              <div class="flex flex-wrap">
                <text-input
                  v-model="newTopic.name"
                  class="pr-6 pb-8 w-full lg:w-1/2"
                  label="Nome"
                />

                <text-input
                  v-model="newTopic.icon"
                  class="pr-6 pb-8 w-full lg:w-1/3"
                  label="Ícone"
                />

                <a
                  :href="route('icons')"
                  target="_blank"
                  class="btn-indigo mt-auto mb-8 ml-2 mr-auto"
                >
                  Ícones
                </a>
              </div>

              <button
                class="btn-indigo"
                type="button"
                :disabled="newTopicRequest"
                @click="storeNewTopic"
              >
                Salvar
              </button>

              <div v-if="newTopicError" class="ml-2 form-error">
                Verifique se todos os campos possuem valor válido.
              </div>
            </div>

            <span class="font-medium"> Selecione o Tópico </span>

            <div class="mt-4 flex flex-wrap">
              <select-input v-model="existingTopic" class="pr-6 pb-8 w-full" label="Tópico">
                <option :value="null" />
                <option v-for="topic in userTopics" :key="topic.id" :value="topic.id">
                  {{ topic.name }}
                </option>
              </select-input>
            </div>

            <button class="btn-indigo" type="button" @click="selectExistingTopic">
              Selecionar
            </button>

            <button class="btn-gray mt-5" type="button" @click="resetTopicForm">Cancelar</button>
          </div>
        </div>

        <!--========== Inteligência ==========-->
        <div v-if="form.intelligence && form.topics.length > 0" class="p-8 -mr-6 -mb-8">
          <label class="form-label">Seções:</label>
          <table class="border-collapse border w-full">
            <thead class="header">
              <tr class="py-3 px-4">
                <th class="border p-2">Seção</th>
                <th class="border p-2">Tópicos</th>
              </tr>
            </thead>
            <tbody class="body">
              <tr v-for="(section, sectionIndex) in form.sections" :key="`section-${sectionIndex}`">
                <td class="border p-4">
                  <span
                    :class="{ 'hover:text-indigo cursor-pointer': editingSection === null }"
                    @click="editSection(sectionIndex)"
                  >
                    {{ section.name }}
                  </span>
                  <button
                    v-if="form.sections.length > 1 && sectionIndex > 0"
                    class="hover:underline ml-5 mr-2 my-auto"
                    tabindex="-1"
                    type="button"
                    @click="destroySection(sectionIndex)"
                  >
                    <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
                  </button>
                </td>
                <td class="border p-4">
                  <ul>
                    <li
                      v-for="(topic, topicIndex) in section.topics"
                      :key="`section-topic-${topic.id}`"
                      class="p-1 my-5"
                    >
                      {{ topic.name }}
                      <button
                        v-if="sectionIndex !== 0"
                        class="hover:underline ml-5 mr-2 my-auto"
                        tabindex="-1"
                        type="button"
                        @click="moveUp(topicIndex, sectionIndex)"
                      >
                        <font-awesome-icon
                          icon="fas fa-chevron-up"
                          size="lg"
                          class="flex-shrink-0 w-4 h-4 text-indigo-500"
                        />
                      </button>

                      <button
                        v-if="form.sections.length !== sectionIndex + 1"
                        class="hover:underline ml-5 mr-2 my-auto"
                        tabindex="-1"
                        type="button"
                        @click="moveDown(topicIndex, sectionIndex)"
                      >
                        <font-awesome-icon
                          icon="fas fa-chevron-down"
                          size="lg"
                          class="flex-shrink-0 w-4 h-4 text-indigo-500"
                        />
                      </button>
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex flex-wrap mb-3">
            <div>
              <div v-if="editingSectionAUX !== null && editingSectionAUX == editingSection">
                <div class="flex flex-wrap">
                  <text-input
                    v-model="currentSection.name"
                    class="pr-6 pb-8 w-full lg:w-1/3"
                    label="Nome"
                  />

                  <button
                    class="btn-indigo w-1/8 h-1/2 mt-6 mr-2 pl-5"
                    type="button"
                    @click="updateCurrentSection"
                  >
                    Salvar
                  </button>

                  <button
                    class="btn-gray w-1/8 h-1/2 mt-6 pl-5"
                    type="button"
                    @click="resetCurrentSectionForm"
                  >
                    Cancelar
                  </button>
                </div>

                <div
                  v-if="form.sections.length > 1 && sectionIndex !== form.sections.length - 1"
                  class="flex flex-wrap gap-5"
                >
                  <select-input
                    v-model="currentSectionConfig.next"
                    class="pr-6 pb-8 w-96"
                    label="Após respondida, ir para"
                  >
                    <option
                      v-for="(formSection, formSectionIndex) in form.sections"
                      :key="formSection.id"
                      :value="formSectionIndex"
                      :disabled="formSectionIndex <= currentSection.index"
                    >
                      {{ formSection.name }}
                    </option>
                  </select-input>

                  <select-input
                    v-model="currentSectionConfig.when"
                    class="pr-6 pb-8 w-96"
                    label="Quando"
                    @change="removeConfigQuestionIfWithoutIf($event)"
                  >
                    <option value="EVER">Sempre</option>
                    <option v-if="currentSection.topics.length > 0" value="IF">Se</option>
                  </select-input>

                  <select-input
                    v-if="currentSectionConfig.when === 'IF'"
                    v-model="currentSectionConfig.question"
                    class="pr-6 pb-8 w-96"
                    label="Questão"
                  >
                    <option
                      v-for="currentSectionQuestion in currentSectionQuestions"
                      :key="`section-question-${currentSectionQuestion.id}`"
                      :value="currentSectionQuestion.id"
                    >
                      {{ currentSectionQuestion.statement }}
                    </option>
                  </select-input>

                  <select-input
                    v-if="currentSectionConfigQuestionIsNumber"
                    v-model="currentSectionConfig.number"
                    class="pr-6 pb-8 w-96"
                    label="Tem valor"
                  >
                    <option
                      v-for="number in [1, 2, 3, 4, 5]"
                      :key="`section-question-number-${number}`"
                      :value="number"
                    >
                      {{ number }}
                    </option>
                  </select-input>

                  <select-input
                    v-if="currentSectionConfigQuestionIsText"
                    v-model="currentSectionConfig.text"
                    class="pr-6 pb-8 w-96"
                    label="Tem valor"
                  >
                    <option
                      v-for="alternative in currentSectionQuestionAlternatives"
                      :key="`section-question-alternative-${alternative.id}`"
                      :value="alternative.alternative"
                    >
                      {{ alternative.alternative }}
                    </option>
                  </select-input>
                </div>
              </div>

              <br />
              <br />
            </div>
          </div>

          <button
            v-if="form.topics.length > form.sections.length"
            class="btn-gray"
            type="button"
            @click="addNewSection"
          >
            + Seção
          </button>
        </div>

        <div
          class="px-8 py-4 mt-5 bg-gray-50 border-t border-gray-100 flex justify-end items-center"
        >
          <button class="text-red-600 mr-4 hover:underline" type="button" @click="reset">
            Zerar Questionário
          </button>

          <button class="text-red-600 mr-4 hover:underline" type="button" @click="destroy">
            Deletar Questionário
          </button>

          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            Salvar modificações
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import LoadingButton from '@/Shared/LoadingButton'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import axios from 'axios'
import TextareaInput from '@/Shared/TextareaInput'
import FileInput from '@/Shared/FileInput'
import FileInputBase64 from '@/Shared/FIleInputBase64'
import Modal from './Modal'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'

export default {
  metaInfo() {
    return {
      title: this.form.title,
    }
  },
  components: {
    Icon,
    LoadingButton,
    TextInput,
    SelectInput,
    TextareaInput,
    // FileInput,
    FileInputBase64,
    Modal,
    Accordion,
    AccordionTab,
  },
  layout: Layout,
  remember: 'form',
  props: {
    states: Array,
    cities: Array,
    topics: Array,
    organizations: Array,
    roles: Array,
    questionnaire: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        title: this.questionnaire.title,
        opened_on: this.questionnaire.opened_on.split('T')[0],
        state: this.questionnaire.state_id ?? (this.states.length == 1 ? this.states[0] : null),
        city: this.questionnaire.city_id ?? (this.cities.length == 1 ? this.cities[0] : null),
        topics: this.questionnaire.topics,
        organization_id: this.questionnaire.organization_id,
        role_id: this.questionnaire.role_id,
        header: this.questionnaire.header,
        intelligence: this.questionnaire.intelligent,
        sections: this.questionnaire.sections,
        logo: null,
        footer: this.questionnaire.footer,
        hasChangedIMG: false,
      }),
      copied: false,
      topicsData: this.questionnaire.topicsData,
      newTopic: {
        name: null,
        icon: null,
      },
      currentTopic: {
        id: null,
        icon: null,
        name: null,
        index: null,
      },
      newQuestion: {
        statement: '',
        left_label: null,
        right_label: null,
        middle_text: null,
        placeholder: null,
        type: null,
        required: false,
        options: null,
        topic: null,
        text0: '',
        text1: '',
        text2: '',
        text3: '',
        text4: '',
        text5: '',
      },
      currentQuestion: {
        id: null,
        topic: null,
        statement: '',
        left_text: null,
        right_text: null,
        middle_text: null,
        placeholder: null,
        type: null,
        required: false,
        options: null,
        index: null,
        text0: '',
        text1: '',
        text2: '',
        text3: '',
        text4: '',
        text5: '',
      },
      newAlternative: {
        option: null,
      },
      currentAlternative: {
        id: null,
        option: null,
        topic: null,
        question: null,
        index: null,
      },
      currentSection: {
        id: null,
        name: null,
        topics: null,
        index: null,
      },
      currentSectionConfig: {
        next: null,
        when: null,
        question: null,
        number: null,
        text: null,
      },
      newTopicRequest: false,
      currentTopicRequest: false,
      newQuestionRequest: false,
      currentQuestionRequest: false,
      newTopicError: false,
      newQuestionError: false,
      newQuestionStatement: false,
      currentQuestionStatement: false,
      existingTopic: null,
      creatingAlternative: false,
      selectingTopicOption: false,
      selectingQuestionOption: false,
      editingTopic: null,
      editingQuestion: null,
      editingAlternative: false,
      editingSection: null,
      userCities: this.cities,
      $this: this,
      get userTopics() {
        return this.$this.topics.filter((topic) => !this.form.topics.includes(topic.id))
      },
      get currentSectionQuestions() {
        const questions = []

        for (const topic of this.currentSection.topics) {
          for (const question of topic.questionsData) {
            questions.push(question)
          }
        }

        return questions
      },
      get currentSectionConfigQuestionIsNumber() {
        for (const question of this.currentSectionQuestions) {
          if (question.id === this.currentSectionConfig.question) {
            if (question.type === 'NUMBER') {
              return true
            }
          }
        }

        return false
      },
      get currentSectionConfigQuestionIsText() {
        for (const question of this.currentSectionQuestions) {
          if (question.id === this.currentSectionConfig.question) {
            if (question.type === 'CHECK' || question.type === 'RADIO') {
              return true
            }
          }
        }

        return false
      },
      get currentSectionQuestionAlternatives() {
        for (const question of this.currentSectionQuestions) {
          if (question.id === this.currentSectionConfig.question) {
            return question.options
          }
        }

        return []
      },
      sectionIndexAUX: '',
      editingSectionAUX: '',

      flagAuxEditExistingOption: false,

      newInstructionsStatement: false, //to show modal for instructions
      modal_footer: false, // to show modal for footer
      newQuestionStatement_aux: false, //to show statement for new question modal

      propForInputBase64: {},
      currentFile: {
        name: 'null',
        data64: 'null',
      },
    }
  },
  mounted() {
    ;(this.propForInputBase64.isBeingEdited = true),
      (async () => {
        if (this.questionnaire.logo) {
          const res = await fetch(this.questionnaire.logo)
          const blob = await res.blob()

          console.log('ciau aqui')

          const reader = new FileReader()
          reader.onload = () => {
            const base64String = reader.result.split(',')[1] // Obtém a parte do base64 da string resultante

            console.log(reader.result)
            console.log(base64String)
            this.form.logo = base64String

            const fileName = this.questionnaire.logo.split('/').at(-1)
            const fileSize = blob.size

            console.log(this.form.logo)
            console.log(this.questionnaire)
            console.log(fileName)
            console.log(fileSize)
            // propForBase64Input.name = fileName
            // propForBase64Input.size = fileSize

            this.propForInputBase64.isBeingEdited = true
            this.propForInputBase64.name = fileName
            this.propForInputBase64.size = fileSize

            this.currentFile.name = fileName
            this.currentFile.data64 = base64String
          }

          console.log(this.propForInputBase64)
          console.log(this.currentFile)

          reader.readAsDataURL(blob)
        }
      })()

    console.log(this.form)
  },
  methods: {
    storeNewTopic() {
      this.newTopicError = false
      this.newTopicRequest = true

      axios
        .post(this.route('topics.store'), {
          ...this.newTopic,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then((response) => {
          const data = response.data

          const topic = data.resource.topic

          this.form.topics.push(topic.id)
          this.topicsData.push({ ...topic, questionsData: [] })

          if (this.form.intelligence) {
            if (!this.form.sections[0]) {
              this.form.sections[0] = {
                name: 'Seção 1',
                topics: [],
                config: {
                  next: 1,
                  when: 'EVER',
                  question: null,
                  number: null,
                  text: null,
                },
              }
            }

            this.form.sections[0].topics.push(this.topicsData[this.topicsData.length - 1])
          }

          this.newTopicRequest = false

          this.resetTopicForm()
        })
        .catch(() => {
          this.newTopicError = true

          this.newTopicRequest = false
        })
    },
    selectExistingTopic() {
      if (!this.existingTopic) {
        return
      }

      this.form.topics.push(this.existingTopic)

      this.topics.forEach((topic) => {
        if (topic.id === this.existingTopic) {
          this.topicsData.push({ ...topic, questionsData: topic.questions })

          if (this.form.intelligence) {
            if (!this.form.sections[0]) {
              this.form.sections[0] = {
                name: 'Seção 1',
                topics: [],
                config: {
                  next: 1,
                  when: 'EVER',
                  question: null,
                  number: null,
                  text: null,
                },
              }
            }

            this.form.sections[0].topics.push(this.topicsData[this.topicsData.length - 1])
          }
        }
      })

      this.resetTopicForm()
    },
    resetTopicForm() {
      this.selectingTopicOption = false

      this.newTopic.name = null
      this.newTopic.icon = null

      this.existingTopic = null

      this.newTopicError = false
    },
    editTopic(topic_id, icon, name, index) {
      if (this.editingTopic) {
        return
      }

      this.currentTopic.id = topic_id
      this.currentTopic.icon = icon
      this.currentTopic.name = name
      this.currentTopic.index = index

      this.editingTopic = topic_id
    },
    updateCurrentTopic() {
      this.currentTopicRequest = true

      axios
        .put(this.route('topics.update', { id: this.currentTopic.id }), {
          ...this.currentTopic,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then(() => {
          this.topicsData[this.currentTopic.index].icon = this.currentTopic.icon
          this.topicsData[this.currentTopic.index].name = this.currentTopic.name

          this.currentTopicRequest = false

          this.resetCurrentTopicForm()
        })
        .catch(() => {
          this.currentTopicRequest = false
        })
    },
    resetCurrentTopicForm() {
      this.editingTopic = null

      this.currentTopic.id = null
      this.currentTopic.icon = null
      this.currentTopic.name = null
      this.currentTopic.index = null
    },
    makeNewQuestion(topic_id) {
      this.selectingQuestionOption = true

      this.newQuestion.topic = topic_id
    },
    openNewQuestionStatement() {
      this.newQuestionStatement = true
    },
    openCurrentQuestionStatement() {
      this.currentQuestionStatement = true
    },
    setNewQuestionPlaceholder(statement) {
      this.newQuestion.statement = statement
      this.newQuestion.placeholder = statement.split(/\n/g)[0]

      this.newQuestionStatement = false
    },
    setCurrentQuestionPlaceholder(statement) {
      this.currentQuestion.statement = statement
      this.currentQuestion.placeholder = statement.split(/\n/g)[0]

      this.currentQuestionStatement = false
    },
    invertNewQuestionRequeriment() {
      this.newQuestion.required = !this.newQuestion.required
    },
    invertCurrentQuestionRequeriment() {
      this.currentQuestion.required = !this.currentQuestion.required
    },
    editQuestion(
      question_id,
      topic_id,
      statement,
      left_text,
      right_text,
      middle_text,
      type,
      required,
      options,
      text0,
      text1,
      text2,
      text3,
      text4,
      text5,
      index,
    ) {
      if (this.editingQuestion) {
        return
      }

      this.currentQuestion.id = question_id
      this.currentQuestion.topic = topic_id
      this.currentQuestion.statement = statement
      this.currentQuestion.left_text = left_text
      this.currentQuestion.right_text = right_text
      this.currentQuestion.middle_text = middle_text
      this.currentQuestion.placeholder = statement.split(/\n/g)[0]
      this.currentQuestion.type = type
      this.currentQuestion.required = required
      this.currentQuestion.options = options
      this.currentQuestion.index = index
      this.currentQuestion.text0 = text0
      this.currentQuestion.text1 = text1
      this.currentQuestion.text2 = text2
      this.currentQuestion.text3 = text3
      this.currentQuestion.text4 = text4
      this.currentQuestion.text5 = text5
      this.editingQuestion = question_id
    },
    storeNewQuestion() {
      this.newQuestionError = false
      this.newQuestionRequest = true

      axios
        .post(this.route('questions.store'), {
          ...this.newQuestion,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then((response) => {
          const data = response.data

          const question = data.resource.question

          this.topicsData.forEach((topic) => {
            if (topic.id === question.topic_id) {
              topic.questionsData.push(question)
            }
          })

          this.newQuestionRequest = false

          this.resetQuestionForm()
        })
        .catch(() => {
          this.newQuestionError = true

          this.newQuestionRequest = false
        })
    },
    addOptionToNewQuestion() {
      if (this.creatingAlternative) {
        return
      }

      this.newAlternative.option = null

      this.creatingAlternative = true
    },
    storeNewQuestionOption() {
      this.newAlternative.option = this.newAlternative.option?.trim()

      if (this.newAlternative.option === '') {
        return
      }

      if (!this.newQuestion.options) {
        this.newQuestion.options = []
      }

      this.newQuestion.options.push(this.newAlternative.option)

      this.creatingAlternative = false

      this.newAlternative.option = null
    },
    storeNewQuestionCurrentOption() {
      this.currentAlternative.option = this.currentAlternative.option?.trim()

      if (this.currentAlternative.option === '') {
        return
      }

      this.newQuestion.options[this.currentAlternative.index] = this.currentAlternative.option

      this.editingAlternative = false

      this.currentAlternative.index = null
      this.currentAlternative.option = null
    },
    editNewQuestionOption(option, index) {
      if (this.currentAlternative.option || this.currentAlternative.index) {
        return
      }

      this.editingAlternative = true

      this.currentAlternative.option = option
      this.currentAlternative.index = index
    },
    destroyNewQuestionOption(index) {
      this.newQuestion.options.splice(index, 1)
    },
    resetQuestionForm() {
      this.selectingQuestionOption = false

      this.newQuestion.statement = ''
      this.newQuestion.left_label = ''
      this.newQuestion.right_label = ''
      this.newQuestion.middle_text = ''
      this.newQuestion.placeholder = null
      this.newQuestion.type = null
      this.newQuestion.required = false
      this.newQuestion.options = null
      this.newQuestion.topic = null

      this.newQuestion.text0 = ''
      this.newQuestion.text1 = ''
      this.newQuestion.text2 = ''
      this.newQuestion.text3 = ''
      this.newQuestion.text4 = ''
      this.newQuestion.text5 = ''

      this.newQuestionError = false
    },
    updateCurrentQuestion() {
      this.currentQuestionRequest = true

      axios
        .put(this.route('questions.update', { id: this.currentQuestion.id }), {
          ...this.currentQuestion,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then(() => {
          this.topicsData.forEach((topic) => {
            if (topic.id === this.currentQuestion.topic) {
              topic.questionsData[this.currentQuestion.index].statement =
                this.currentQuestion.statement
              topic.questionsData[this.currentQuestion.index].left_label =
                this.currentQuestion.left_text
              topic.questionsData[this.currentQuestion.index].right_label =
                this.currentQuestion.right_text
              topic.questionsData[this.currentQuestion.index].middle_text =
                this.currentQuestion.middle_text
              topic.questionsData[this.currentQuestion.index].type = this.currentQuestion.type
              topic.questionsData[this.currentQuestion.index].required =
                this.currentQuestion.required
              topic.questionsData[this.currentQuestion.index].options = this.currentQuestion.options

              topic.questionsData[this.currentQuestion.index].slider_text_zero =
                this.currentQuestion.text0
              topic.questionsData[this.currentQuestion.index].slider_text_one =
                this.currentQuestion.text1
              topic.questionsData[this.currentQuestion.index].slider_text_two =
                this.currentQuestion.text2
              topic.questionsData[this.currentQuestion.index].slider_text_three =
                this.currentQuestion.text3
              topic.questionsData[this.currentQuestion.index].slider_text_four =
                this.currentQuestion.text4
              topic.questionsData[this.currentQuestion.index].slider_text_five =
                this.currentQuestion.text5
            }
          })

          this.currentQuestionRequest = false

          this.resetCurrentQuestionForm()
        })
        .catch(() => {
          this.currentQuestionRequest = false
        })
    },
    resetCurrentQuestionForm() {
      this.editingQuestion = null

      this.currentQuestion.id = null
      this.currentQuestion.topic = null
      this.currentQuestion.statement = null
      this.currentQuestion.left_text = null
      this.currentQuestion.right_text = null
      this.currentQuestion.middle_text = null
      this.currentQuestion.type = null
      this.currentQuestion.options = null
      this.currentQuestion.index = null
      this.currentQuestion.text0 = null
      this.currentQuestion.text1 = null
      this.currentQuestion.text2 = null
      this.currentQuestion.text3 = null
      this.currentQuestion.text4 = null
      this.currentQuestion.text5 = null
    },
    editQuestionOption(optionIndex, questionIndex, topicIndex) {
      if (!this.editingQuestion) {
        return
      }

      const topic = this.topicsData[topicIndex]
      const question = topic.questionsData[questionIndex]
      const option = question.options[optionIndex]

      this.currentAlternative.id = option.id
      this.currentAlternative.option = option.alternative
      this.currentAlternative.topic = topic.id
      this.currentAlternative.question = question.id
      this.currentAlternative.index = optionIndex

      this.editingAlternative = true
      this.flagAuxEditExistingOption = true
    },
    destroyQuestionOption(id, index, question) {
      if (
        confirm(
          'Tem certeza de que deseja excluir esta opção?' +
            ' Essa ação poderá afetar outros questionários!',
        )
      ) {
        axios
          .post(this.route('questions.option.destroy', { id }), {
            _method: 'DELETE',
            _token: this.$page.props.auth.user.csrf_token,
          })
          .then(() => question.options.splice(index, 1))
      }
    },
    updateCurrentAlternative() {
      axios
        .put(this.route('questions.options.update', { id: this.currentAlternative.id }), {
          ...this.currentAlternative,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then(
          this.topicsData.forEach((topic) => {
            if (topic.id === this.currentAlternative.topic) {
              topic.questionsData.forEach((question) => {
                if (question.id === this.currentAlternative.question) {
                  question.options.forEach((option) => {
                    if (option.id === this.currentAlternative.id) {
                      option.alternative = this.currentAlternative.option

                      this.currentAlternative.id = null
                      this.currentAlternative.option = null
                      this.currentAlternative.topic = null
                      this.currentAlternative.question = null
                      this.currentAlternative.index = null

                      this.editingAlternative = false
                    }
                  })
                }
              })
            }
          }),
        )
    },
    update() {
      if (this.form.intelligence) {
        for (const section of this.form.sections) {
          if (section.topics.length === 0) {
            return
          }
        }
      }

      console.log(this.currentFile)
      console.log(this.form.logo) //base 64 data
      console.log(this.questionnaire.logo) // name

      let actualFileName

      if (this.questionnaire.logo) {
        actualFileName = this.questionnaire.logo.split('/').pop()
      }
      let actualFileData = this.form.logo

      console.log('\n')
      console.log('\n')
      console.log(actualFileName)
      console.log(actualFileData)

      if (this.currentFile.name == actualFileName && this.currentFile.data64 == actualFileData) {
        this.form.hasChangedIMG = false
        console.log('é igual')
        console.log(this.form.hasChangedIMG)
      } else {
        this.form.hasChangedIMG = true
        console.log('é diferente')
        console.log(this.form.hasChangedIMG)
      }

      this.form.post(this.route('questionnaires.update', this.questionnaire.id).url())
    },
    destroy() {
      if (confirm('Tem certeza de que deseja excluir este questionário?')) {
        this.$inertia.delete(this.route('questionnaires.destroy', this.questionnaire.id))
      }
    },
    destroyTopic(id, index) {
      console.log('Topic_id = ', id)
      console.log('Topic_index = ', index)

      if (
        confirm(
          'Tem certeza de que deseja excluir este tópico?' +
            ' Essa ação poderá afetar outros questionários!',
        )
      ) {
        axios
          .push(this.route('topics.destroy', { id }), {
            _method: 'DELETE',
            _token: this.$page.props.auth.user.csrf_token,
          })
          .then(() => {
            this.topicsData.splice(index, 1)

            this.form.sections.forEach(
              (section) => (section.topics = section.topics.filter((topic) => topic.id !== id)),
            )
          })
      }
    },
    removeTopic(id) {
      axios
        .post(this.route('topics.remove', { id }), {
          _method: 'DELETE',
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then((data) => {
          this.$inertia.get(this.route('questionnaires.edit', this.questionnaire.id))
        })
    },
    destroyQuestion(id, topic_id, index) {
      if (
        confirm(
          'Tem certeza de que deseja excluir esta questão?' +
            ' Essa ação poderá afetar outros questionários!',
        )
      ) {
        axios
          .post(this.route('questions.destroy', { id }), {
            _method: 'DELETE',
            _token: this.$page.props.auth.user.csrf_token,
          })
          .then(() => {
            this.topicsData.forEach((topic) => {
              if (topic.id === topic_id) {
                topic.questionsData.splice(index, 1)
              }
            })
          })
      }
    },
    removeQuestionOptions(e, id, original) {
      const eValue = e.target.value
      const current = this.currentQuestion

      const alterTypeFromRadioOrCheck =
        (original === 'RADIO' || original === 'CHECK') &&
        (eValue === 'NUMBER' || eValue === '' || current.type === 'NUMBER' || !current.type)

      const alterTypeFromNumber =
        original === 'NUMBER' &&
        (eValue === 'RADIO' ||
          eValue === 'CHECK' ||
          eValue === '' ||
          current.type === 'RADIO' ||
          current.type === 'CHECK' ||
          !current.type)

      if (alterTypeFromRadioOrCheck || alterTypeFromNumber) {
        if (
          confirm(
            'Tem certeza de que deseja alterar o tipo de questão?' + ' Essa ação é irreversível!',
          )
        ) {
          this.currentQuestionRequest = true

          axios
            .post(this.route('questions.options.destroy', { id }), {
              _method: 'DELETE',
              _token: this.$page.props.auth.user.csrf_token,
              type: eValue,
            })
            .then(() => {
              for (const topic of this.topicsData) {
                for (const question of topic.questionsData) {
                  if (question.id === id) {
                    question.type = eValue
                    question.options = null
                  }
                }
              }

              this.currentQuestionRequest = false
            })
            .catch(() => (this.currentQuestionRequest = false))

          return
        }

        this.resetCurrentQuestionForm()
      }
    },
    reset() {
      if (confirm('Tem certeza de que deseja excluir todas as respostas deste questionário?')) {
        this.$inertia.delete(this.route('questionnaires.reset', this.questionnaire.id))
      }
    },
    copy(url) {
      this.copied = true

      navigator.clipboard.writeText(url)
    },
    getCities() {
      axios
        .get(this.route('cities'), {
          params: {
            stateId: this.form.state,
          },
        })
        .then((response) => {
          this.form.city = null

          this.userCities = response.data
        })
    },
    setIntelligence() {
      if (this.form.intelligence) {
        if (
          !confirm(
            'Você realmente quer desabilitar a inteligência do questionário?' +
              ' Essa ação é irreversível!',
          )
        ) {
          return
        }
      }

      axios
        .post(this.route('questionnaires.intelligence', { id: this.questionnaire.id }), {
          _method: 'PUT',
          _token: this.$page.props.auth.user.csrf_token,
          intelligence: !this.form.intelligence,
        })
        .then(() => {
          document.getElementById('f_intelligence').click()

          if (!this.form.intelligence) {
            this.form.sections = null
            this.form.logo = null

            return
          }

          this.form.header = null
          this.form.sections = []

          for (const topic_id of this.form.topics) {
            for (const topic of this.topicsData) {
              if (topic_id === topic.id) {
                if (!this.form.sections[0]) {
                  this.form.sections[0] = {
                    name: 'Seção 1',
                    topics: [],
                    config: {
                      next: 1,
                      when: 'EVER',
                      question: null,
                      number: null,
                      text: null,
                    },
                  }
                }

                this.form.sections[0].topics.push(topic)
              }
            }
          }
        })
    },
    addNewSection() {
      const section = {
        name: `Seção ${this.form.sections.length + 1}`,
        topics: [],
      }

      this.form.sections.push(section)

      for (const sectionIndex in this.form.sections) {
        const formSection = this.form.sections[sectionIndex]

        if (!formSection.config) {
          this.form.sections[sectionIndex].config = {
            next: Number(sectionIndex) + 1,
            when: 'EVER',
            question: null,
            number: null,
            text: null,
          }
        }
      }
    },
    editSection(index) {
      if (this.editingSection !== null) {
        return
      }

      this.editingSection = index
      this.editingSectionAUX = this.editingSection
      this.sectionIndexAUX = index

      this.currentSection.name = this.form.sections[this.editingSection].name
      this.currentSection.topics = this.form.sections[this.editingSection].topics

      if (this.form.sections[this.editingSection].config) {
        this.currentSectionConfig = { ...this.form.sections[this.editingSection].config }
      }

      this.currentSection.index = this.editingSection
    },
    updateCurrentSection() {
      const currentSection = this.form.sections[this.editingSection]

      currentSection.name = this.currentSection.name

      if (currentSection.config) {
        currentSection.config = { ...this.currentSectionConfig }
      }

      this.resetCurrentSectionForm()
    },
    resetCurrentSectionForm() {
      this.editingSection = null

      this.currentSection.id = null
      this.currentSection.name = null
      this.currentSection.topics = null
      this.currentSection.index = null

      this.currentSectionConfig.next = null
      this.currentSectionConfig.when = null
      this.currentSectionConfig.question = null
      this.currentSectionConfig.number = null
      this.currentSectionConfig.text = null
    },
    removeConfigQuestionIfWithoutIf(event) {
      if (event.target.value === 'EVER') {
        this.currentSectionConfig.question = null
        this.currentSectionConfig.number = null
        this.currentSectionConfig.text = null
      }
    },
    destroySection(index) {
      this.form.sections[index - 1].topics.push(...this.form.sections[index].topics)

      this.form.sections.splice(index, 1)
    },
    moveDown(topicIndex, sectionIndex) {
      this.form.sections[sectionIndex + 1].topics.push(
        this.form.sections[sectionIndex].topics[topicIndex],
      )

      this.form.sections[sectionIndex].topics.splice(topicIndex, 1)
    },
    moveUp(topicIndex, sectionIndex) {
      this.form.sections[sectionIndex - 1].topics.push(
        this.form.sections[sectionIndex].topics[topicIndex],
      )

      this.form.sections[sectionIndex].topics.splice(topicIndex, 1)
    },
    getQuestionType(id_type) {
      switch (id_type) {
        case 'NUMBER':
          return 'Nota (1 a 5)'
        case 'RADIO':
          return 'Escolha Única'
        case 'CHECK':
          return 'Seleção Múltipla'
        case 'LINE':
          return 'Texto de Linha Única'
        case 'MULTI':
          return 'Texto Multilinha'
        case 'SLIDER':
          return 'Controle Deslizante'
      }
    },
    openNewInstruction() {
      this.newInstructionsStatement = true
    },
    setNewInstructionStatementPlaceholder(statement) {
      this.form.header = statement
      this.newInstructionsStatement = false
    },
    showModalFooter() {
      this.modal_footer = true
    },
    setModal_footer_statement(statement) {
      this.form.footer = statement
      this.modal_footer = false
    },
    showModalNewQuestionStatement() {
      this.newQuestionStatement_aux = true
    },
    setModal_newQuestionStatement(statment) {
      this.newQuestion.statement = statment
      this.newQuestionStatement_aux = false
    },
  },
}
</script>
